// DropIn.js
import React, { useEffect, useState , useRef, useCallback } from 'react';
import { createElement, loadAirwallex } from 'airwallex-payment-elements';
import { useDispatch, useSelector } from 'react-redux';
import { setRecordPaymentResponse } from '../../redux/actions/recordPaymentActions';
import ReactGA from "react-ga4";
import { useNavigate } from 'react-router-dom';



const DropIn = () => {
  const dispatch = useDispatch();
  const [elementShow, setElementShow] = useState(false);
  const isPaymentRecordedRef = useRef(false); // Use ref payment recorded state

  const [errorMessage, setErrorMessage] = useState('');
  const intentResponse = useSelector(state => state.createIntent);
  const createBookingResponse = useSelector(state => state.createBooking);
  const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;
  const { totalCost } = useSelector((state) => state.booking);
  const navigate = useNavigate();
  const intent_id = intentResponse.intentId;
  const client_secret = intentResponse.clientSecret;
  const currency = intentResponse.currencyCode;
  const bookingId = intentResponse.bookingId;
  const amount = intentResponse.amount;
  const isAirwallexMounted = useRef(false);

  useEffect(() => {
    if (!intent_id && !isAirwallexMounted.current) return;
    loadAirwallex({
      env: process.env.REACT_APP_AIRWALLEX_ENV,
      origin: window.location.origin,
    }).then(() => {
      const dropIn = createElement('dropIn', {
        intent_id: intent_id,
        client_secret: client_secret,
        currency: currency,
        disallowed_methods: ['klarna', 'directdebit', 'bitpay', 'skrill', 'trustly'], // Exclude unwanted methods
        methods: ['googlepay', 'card', 'paypal', 'applepay'], // Restrict to GPay, cards, and PayPal
        applePayRequestOptions: {
          countryCode: 'GB',
          currencyCode: currency,
          totalPriceLabel: 'LiftsTo'
        },
      });
      dropIn.mount('airwallex-dropin-container');
      isAirwallexMounted.current = true;
      setElementShow(true);
    });
  }, [intent_id, client_secret, currency, isAirwallexMounted]);

  const onError = useCallback((event) => {
    const { error } = event.detail;
    console.error('❌ Airwallex Error:', error);

    isPaymentRecordedRef.current = false; // reset the payment flag

    // Known form errors you DON'T want to redirect for
    const knownFormErrors = [
      'card_declined',
      'risk_declined',
      'invalid_card_number',
      'invalid_cvc',
      'invalid_expiry_date',
      'invalid_expiry_month',
      'invalid_expiry_year',
      'insufficient_funds',
      'authentication_required',
      'authentication_failed',
      'payment_method_not_supported',
      'processing_error',
      'issuer_unavailable',
      'incorrect_number',
      'incorrect_cvc',
      'expired_card',
      'payment_method_declined',
      'temporary_issue_try_again',
    ];

    if (knownFormErrors.includes(error.code)) {
      // Let Airwallex handle it on the form UI
      setErrorMessage(error.message);
      return;
    }

    // Redirect or handle unexpected errors
    setErrorMessage(error.message);
    // navigate('/success/false'); // uncomment if you want to redirect
  }, [setErrorMessage]);

  const onSuccess = useCallback((event) => {

    // Prevents duplicate API calls
    if (isPaymentRecordedRef.current) return;

    isPaymentRecordedRef.current = true; // Set flag to true after first API call
    console.log('Payment Successful:', event.detail);

    const eventDetail = event.detail;
    const transactionId = eventDetail.intent.latest_payment_attempt.id;
    // console.log("✅ Payment Success - Full Event Detail:", eventDetail); // 👈 Log everything

    const paymentDetails = {
      "bookingId": bookingId,
      "transactionId": transactionId,
      "intentId": intent_id,
      "amount": amount,
      "currencyCode": currency
    };
    const headers = {
      'Content-Type': 'application/json'
    };

    if (eventDetail.intent.status === 'SUCCEEDED') {
      fetch(`${apiBaseUrl}/basket/recordPayment`, {
        method: 'POST',
        headers: headers,
        body: JSON.stringify(paymentDetails),
      })
          .then((response) => response.json())
          .then((data) => {
            dispatch(setRecordPaymentResponse(data));

            window.dataLayer = window.dataLayer || [];
            window.dataLayer.push({ ecommerce: null });

            ReactGA.event({
              event: 'purchase',
              ecommerce: {
                transaction_id: createBookingResponse.basket.bookingId,
                affiliation: 'Online Store',
                value: totalCost,
                currency: createBookingResponse.basket.currency[0],
                tax: 0,
                shipping: 0,
                coupon: createBookingResponse.basket.promoCode,
                items: createBookingResponse.basket.transfers.map(transfer => ({
                  item_name: `${transfer.fromDestination.name} to ${transfer.toDestination.name}`,
                  item_id: transfer.id,
                  price: transfer.price,
                  item_brand: transfer.brand,
                  item_category: transfer.transferType.description,
                  item_variant: transfer.variant,
                  quantity: 1
                }))
              }
            });
          })
          .catch((err) => {
            console.error("Payment recording failed:", err);
          });

      navigate('/success/true');
    } else {
      onError(event);
    }

  }, [onError, totalCost, bookingId, intent_id, currency, dispatch, navigate, amount, apiBaseUrl, createBookingResponse]);


  useEffect(() => {
    window.addEventListener('onSuccess', onSuccess);
    window.addEventListener('onError', onError);

    return () => {
      window.removeEventListener('onSuccess', onSuccess);
      window.removeEventListener('onError', onError);
    };
  }, [onSuccess, onError]);

  useEffect(() => {
    isPaymentRecordedRef.current = false;
  }, []);

  return (
      <div>
        {!elementShow && <p>Loading...</p>}
        {errorMessage && <p style={{ color: 'red' }}>{errorMessage}</p>}
        {/* This is the container where Airwallex's drop-in element will be mounted */}
        <div id="airwallex-dropin-container" style={{ width: '100%', height: '100%', margin: '20px 0' }} />
      </div>
  );
};

export default DropIn;